import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../../lib/api";

const TRANSITIONS = [
  { v: "sun_mask", l: "Sun mask (ProForm red)" },
  { v: "crossfade", l: "Layered crossfade" },
  { v: "diagonal_sweep", l: "Diagonal light sweep" },
  { v: "focus", l: "Slow focus" },
];
const ALIGNMENTS = ["left", "center", "right"];
const VERTICALS = ["upper", "center", "lower"];
const STATUSES = ["draft", "published"];

const mediaUrl = (u) => (u?.startsWith("/api/") ? `${API.replace("/api", "")}${u}` : u);

function Slider({ label, value, min = 0, max = 100, onChange, onCommit, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="label">{label}</label>
        <span className="font-mono text-[11px] text-chrome-300">{Math.round(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={(e) => onCommit && onCommit(Number(e.target.value))}
        onTouchEnd={(e) => onCommit && onCommit(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-proform-red"
      />
    </div>
  );
}

function FocalPointPicker({ label, url, x, y, onChange, testid }) {
  const ref = useRef(null);
  const onClick = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 100;
    const ny = ((e.clientY - rect.top) / rect.height) * 100;
    onChange(Math.max(0, Math.min(100, nx)), Math.max(0, Math.min(100, ny)));
  };
  return (
    <div>
      <div className="label mb-2">{label} · click to reposition</div>
      <div
        ref={ref}
        onClick={onClick}
        data-testid={testid}
        className="relative aspect-[16/9] w-full border border-ink-700 overflow-hidden bg-ink-900 cursor-crosshair"
      >
        {url ? (
          <img
            src={mediaUrl(url)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ objectPosition: `${x}% ${y}%` }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-chrome-300 text-xs">Upload an image to enable focal-point picker</div>
        )}
        {url && (
          <div
            className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-proform-red shadow-[0_0_0_1px_rgba(0,0,0,0.6)] pointer-events-none"
            style={{ left: `${x}%`, top: `${y}%` }}
          />
        )}
      </div>
      <div className="mt-1 font-mono text-[10px] tracking-[0.24em] text-chrome-300">X {Math.round(x)}% · Y {Math.round(y)}%</div>
    </div>
  );
}

function UploadButton({ variant, label, onUploaded, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savings, setSavings] = useState("");

  const pick = () => inputRef.current?.click();
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setSavings("");
    setBusy(true);
    try {
      const results = [];
      let totalOrig = 0, totalOpt = 0;
      for (const file of files) {
        const fd = new FormData();
        fd.append("variant", variant);
        fd.append("file", file);
        const { data } = await api.post("/admin/hero/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        results.push({ ...data, filename: file.name });
        totalOrig += data.original_size || 0;
        totalOpt += data.size || 0;
      }
      if (totalOrig > 0) {
        const kb = (n) => `${(n / 1024).toFixed(0)} KB`;
        const pct = Math.max(0, Math.round((1 - totalOpt / totalOrig) * 100));
        setSavings(`Optimized: ${kb(totalOrig)} → ${kb(totalOpt)} (−${pct}%)`);
      }
      onUploaded(results);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        onChange={upload}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        data-testid={testid}
        className="btn justify-center disabled:opacity-50"
      >
        {busy ? "Uploading…" : label}
      </button>
      {savings && <span className="text-chrome-300 text-xs">{savings}</span>}
      {err && <span className="text-proform-red text-xs">{err}</span>}
    </div>
  );
}

const emptySlide = () => ({
  title: "",
  internal_label: "",
  desktop_media_url: "",
  tablet_media_url: "",
  mobile_media_url: "",
  alt_text: "",
  vehicle_type: "",
  focal_desktop_x: 50, focal_desktop_y: 50,
  focal_mobile_x: 50, focal_mobile_y: 50,
  text_alignment: "left",
  text_vertical: "center",
  overlay_darkness: 50,
  gradient_left: 80, gradient_right: 22, gradient_bottom: 58,
  red_accent: 60, blue_accent: 15,
  parallax_strength: 40,
  transition_style: "sun_mask",
  duration_ms: 7000,
  sort_order: 0,
  enabled: true,
  status: "published",
  temporary: true,
  source_notes: "",
  cta_url: "",
});

export default function HeroSlidesTab() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hero/slides");
      setSlides(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    // Optimistic
    setSlides((list) => list.map((s) => (s.id === id ? { ...s, ...changes } : s)));
    await api.patch(`/admin/hero/slides/${id}`, changes);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide? This cannot be undone.")) return;
    await api.delete(`/admin/hero/slides/${id}`);
    setSlides((list) => list.filter((s) => s.id !== id));
    if (openId === id) setOpenId(null);
  };

  const duplicate = async (slide) => {
    const clone = { ...slide };
    delete clone.id;
    delete clone.created_at;
    delete clone.updated_at;
    clone.sort_order = 0; // backend will auto-assign to end
    clone.title = `${slide.title || "Untitled"} (copy)`;
    clone.status = "draft";
    const { data } = await api.post("/admin/hero/slides", clone);
    setSlides((list) => [...list, data]);
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ordered = [...slides];
    const from = ordered.findIndex((s) => s.id === dragId);
    const to = ordered.findIndex((s) => s.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    const reindexed = ordered.map((s, i) => ({ ...s, sort_order: i + 1 }));
    setSlides(reindexed);
    setDragId(null);
    await api.post("/admin/hero/slides/reorder", {
      items: reindexed.map((s) => ({ id: s.id, sort_order: s.sort_order })),
    });
  };

  const bulkUpload = async (uploads) => {
    // Each uploaded desktop image creates a fresh slide (published draft-ready).
    for (const up of uploads) {
      const draft = {
        ...emptySlide(),
        title: up.filename?.replace(/\.[^.]+$/, "") || "New slide",
        desktop_media_url: up.url,
        mobile_media_url: up.url,
        alt_text: "",
        status: "draft",
        temporary: true,
      };
      const { data } = await api.post("/admin/hero/slides", draft);
      setSlides((list) => [...list, data]);
    }
  };

  const activeCount = useMemo(
    () => slides.filter((s) => s.enabled && s.status === "published").length,
    [slides]
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display uppercase text-3xl">Hero Slider</h1>
          <p className="text-chrome-300 text-sm mt-1">
            {slides.length} total · {activeCount} live on the homepage · drag rows to reorder
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <UploadButton
            variant="desktop"
            label="+ Bulk upload desktop images"
            onUploaded={bulkUpload}
            testid="hero-bulk-upload"
          />
        </div>
      </div>

      <div className="card p-4 mb-6 text-sm text-chrome-300">
        <div className="label mb-2">Recommended sizes</div>
        Desktop <span className="text-chrome-100">3840×2160</span> (16:9) ·
        Ultrawide <span className="text-chrome-100">3840×1646</span> (21:9) ·
        Tablet <span className="text-chrome-100">2048×1536</span> (4:3) ·
        Mobile portrait <span className="text-chrome-100">1440×1800</span> (4:5) ·
        Tall mobile <span className="text-chrome-100">1440×2560</span> (9:16)
      </div>

      {loading ? (
        <div className="label">Loading…</div>
      ) : (
        <ul className="space-y-3" data-testid="hero-slides-list">
          {slides.map((s) => (
            <li
              key={s.id}
              draggable
              onDragStart={() => setDragId(s.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.id)}
              onDragEnd={() => setDragId(null)}
              className={`border ${dragId === s.id ? "border-proform-red" : "border-ink-700"} bg-ink-900`}
            >
              <div className="flex items-center gap-4 p-3">
                <div className="cursor-grab text-chrome-300 font-mono text-sm px-2 select-none">⋮⋮</div>
                <div className="font-mono text-[11px] text-chrome-300 w-8">{String(s.sort_order).padStart(2, "0")}</div>
                <div className="w-24 h-14 bg-ink-800 flex-shrink-0 overflow-hidden">
                  {s.desktop_media_url ? (
                    <img
                      src={mediaUrl(s.desktop_media_url)}
                      alt=""
                      className="w-full h-full object-cover"
                      style={{ objectPosition: `${s.focal_desktop_x}% ${s.focal_desktop_y}%` }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-chrome-300 text-[10px]">No image</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{s.title || "Untitled"}</div>
                  <div className="label truncate">
                    {s.internal_label || "—"}
                    {s.temporary && <span className="ml-2 text-proform-red">TEMP</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      onChange={(e) => patch(s.id, { enabled: e.target.checked })}
                      className="accent-proform-red"
                      data-testid={`hero-enable-${s.id}`}
                    />
                    <span className="label">Live</span>
                  </label>
                  <select
                    value={s.status}
                    onChange={(e) => patch(s.id, { status: e.target.value })}
                    className="field py-1 text-xs"
                  >
                    {STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <button className="btn" onClick={() => setOpenId(openId === s.id ? null : s.id)} data-testid={`hero-edit-${s.id}`}>
                    {openId === s.id ? "Close" : "Edit"}
                  </button>
                  <button className="label hover:text-proform-red" onClick={() => duplicate(s)}>Duplicate</button>
                  <button className="label hover:text-proform-red" onClick={() => remove(s.id)} data-testid={`hero-delete-${s.id}`}>Delete</button>
                </div>
              </div>

              {openId === s.id && (
                <SlideEditor
                  slide={s}
                  onPatch={(changes) => patch(s.id, changes)}
                />
              )}
            </li>
          ))}
          {slides.length === 0 && (
            <li className="text-chrome-300 text-sm p-4 border border-dashed border-ink-700">
              No slides yet. Use &quot;+ Bulk upload desktop images&quot; above to create your first slide.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function SlideEditor({ slide, onPatch }) {
  const [local, setLocal] = useState(slide);
  useEffect(() => { setLocal(slide); }, [slide.id]);

  const commit = (changes) => {
    setLocal((s) => ({ ...s, ...changes }));
    onPatch(changes);
  };
  const setImmediate = (k, v) => commit({ [k]: v });

  // Debounced-ish: local slider drag only re-renders locally, commit on release
  const setLocalOnly = (changes) => setLocal((s) => ({ ...s, ...changes }));

  return (
    <div className="border-t border-ink-700 p-4 md:p-6 grid md:grid-cols-2 gap-6 bg-ink-950/60">
      {/* Left: metadata + uploads + focal picker */}
      <div className="space-y-5">
        <div>
          <label className="field-label">Title</label>
          <input
            className="field"
            value={local.title || ""}
            onChange={(e) => setLocalOnly({ title: e.target.value })}
            onBlur={(e) => commit({ title: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Internal label</label>
          <input
            className="field"
            value={local.internal_label || ""}
            onChange={(e) => setLocalOnly({ internal_label: e.target.value })}
            onBlur={(e) => commit({ internal_label: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Alt text</label>
          <input
            className="field"
            value={local.alt_text || ""}
            onChange={(e) => setLocalOnly({ alt_text: e.target.value })}
            onBlur={(e) => commit({ alt_text: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <UploadButton
            variant="desktop"
            label={local.desktop_media_url ? "Replace desktop image" : "+ Desktop image"}
            onUploaded={(rs) => commit({ desktop_media_url: rs[0].url })}
            testid={`hero-upload-desktop-${slide.id}`}
          />
          <UploadButton
            variant="mobile"
            label={local.mobile_media_url ? "Replace mobile image" : "+ Mobile image"}
            onUploaded={(rs) => commit({ mobile_media_url: rs[0].url })}
            testid={`hero-upload-mobile-${slide.id}`}
          />
          <UploadButton
            variant="tablet"
            label={local.tablet_media_url ? "Replace tablet image" : "+ Tablet image"}
            onUploaded={(rs) => commit({ tablet_media_url: rs[0].url })}
            testid={`hero-upload-tablet-${slide.id}`}
          />
          <UploadButton
            variant="depth"
            label={local.depth_map_url ? "Replace depth map" : "+ Depth map (opt.)"}
            onUploaded={(rs) => commit({ depth_map_url: rs[0].url })}
            testid={`hero-upload-depth-${slide.id}`}
          />
        </div>

        <FocalPointPicker
          label="Desktop focal point"
          url={local.desktop_media_url}
          x={local.focal_desktop_x}
          y={local.focal_desktop_y}
          onChange={(x, y) => commit({ focal_desktop_x: x, focal_desktop_y: y })}
          testid={`hero-focal-desktop-${slide.id}`}
        />
        <FocalPointPicker
          label="Mobile focal point"
          url={local.mobile_media_url || local.desktop_media_url}
          x={local.focal_mobile_x}
          y={local.focal_mobile_y}
          onChange={(x, y) => commit({ focal_mobile_x: x, focal_mobile_y: y })}
          testid={`hero-focal-mobile-${slide.id}`}
        />
      </div>

      {/* Right: overlay + parallax + transition + meta */}
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Text alignment</label>
            <select className="field" value={local.text_alignment} onChange={(e) => commit({ text_alignment: e.target.value })}>
              {ALIGNMENTS.map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Text vertical</label>
            <select className="field" value={local.text_vertical} onChange={(e) => commit({ text_vertical: e.target.value })}>
              {VERTICALS.map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>
        </div>

        <Slider label="Overlay darkness" value={local.overlay_darkness} onChange={(v) => setLocalOnly({ overlay_darkness: v })} onCommit={(v) => commit({ overlay_darkness: v })} testid={`hero-overlay-${slide.id}`} />
        <Slider label="Left gradient" value={local.gradient_left} onChange={(v) => setLocalOnly({ gradient_left: v })} onCommit={(v) => commit({ gradient_left: v })} />
        <Slider label="Right gradient" value={local.gradient_right} onChange={(v) => setLocalOnly({ gradient_right: v })} onCommit={(v) => commit({ gradient_right: v })} />
        <Slider label="Bottom gradient" value={local.gradient_bottom} onChange={(v) => setLocalOnly({ gradient_bottom: v })} onCommit={(v) => commit({ gradient_bottom: v })} />
        <Slider label="Red accent" value={local.red_accent} onChange={(v) => setLocalOnly({ red_accent: v })} onCommit={(v) => commit({ red_accent: v })} />
        <Slider label="Blue/violet accent" value={local.blue_accent} onChange={(v) => setLocalOnly({ blue_accent: v })} onCommit={(v) => commit({ blue_accent: v })} />
        <Slider label="Parallax strength" value={local.parallax_strength} onChange={(v) => setLocalOnly({ parallax_strength: v })} onCommit={(v) => commit({ parallax_strength: v })} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Transition</label>
            <select className="field" value={local.transition_style} onChange={(e) => commit({ transition_style: e.target.value })}>
              {TRANSITIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Duration (ms)</label>
            <input type="number" min={3000} max={20000} step={500} className="field" value={local.duration_ms}
              onChange={(e) => setLocalOnly({ duration_ms: Number(e.target.value) })}
              onBlur={(e) => commit({ duration_ms: Number(e.target.value) })} />
          </div>
        </div>

        <div>
          <label className="field-label">Source / rights notes</label>
          <textarea className="field" rows={2} value={local.source_notes || ""}
            onChange={(e) => setLocalOnly({ source_notes: e.target.value })}
            onBlur={(e) => commit({ source_notes: e.target.value })} />
        </div>

        <label className="flex items-center gap-2 text-sm text-chrome-300">
          <input type="checkbox" checked={!!local.temporary} onChange={(e) => commit({ temporary: e.target.checked })} className="accent-proform-red" />
          Temporary image (replace with authentic ProForm photography)
        </label>

        <div className="pt-2 flex justify-end">
          <button
            className="btn btn-primary"
            onClick={() => {
              // Commit sliders (they were setLocalOnly during drag)
              const drift = {};
              const keys = ["overlay_darkness","gradient_left","gradient_right","gradient_bottom","red_accent","blue_accent","parallax_strength"];
              for (const k of keys) if (local[k] !== slide[k]) drift[k] = local[k];
              if (Object.keys(drift).length) onPatch(drift);
            }}
            data-testid={`hero-save-${slide.id}`}
          >
            Save overlay settings
          </button>
        </div>
      </div>
    </div>
  );
}
